type AppEnvironment = "local" | "development" | "staging" | "production" | "test";

function text(name: keyof ImportMetaEnv, fallback: string) {
  const value = import.meta.env[name];
  return typeof value === "string" && value.trim() !== ""
    ? value.trim()
    : fallback;
}

function positiveNumber(name: keyof ImportMetaEnv, fallback: number) {
  const value = Number(import.meta.env[name]);
  return Number.isFinite(value) && value > 0 ? value : fallback;
}

function url(name: keyof ImportMetaEnv, fallback: string) {
  const value = text(name, fallback).replace(/\/$/, "");
  try {
    return new URL(value).toString().replace(/\/$/, "");
  } catch {
    throw new Error(`${name} must be an absolute URL.`);
  }
}

function environment(value: string): AppEnvironment {
  const allowed: AppEnvironment[] = [
    "local",
    "development",
    "staging",
    "production",
    "test",
  ];
  return allowed.includes(value as AppEnvironment)
    ? (value as AppEnvironment)
    : "development";
}

function features(value: string) {
  return new Set(
    value
      .split(",")
      .map((feature) => feature.trim())
      .filter(Boolean),
  );
}

export const appConfig = Object.freeze({
  apiBaseUrl: url("VITE_API_BASE_URL", "http://localhost:8080/api/v1"),
  apiTimeoutMs: positiveNumber("VITE_API_TIMEOUT_MS", 15_000),
  environment: environment(text("VITE_APP_ENV", "development")),
  releaseVersion: text("VITE_RELEASE_VERSION", "development"),
  features: features(text("VITE_ADMIN_FEATURES", "foundation")),
  reverb: Object.freeze({
    key: text("VITE_REVERB_APP_KEY", "tawzeef-local-key"),
    host: text("VITE_REVERB_HOST", "localhost"),
    port: positiveNumber("VITE_REVERB_PORT", 8080),
    scheme: text("VITE_REVERB_SCHEME", "http"),
  }),
});

declare global {
  interface ImportMetaEnv {
    readonly VITE_API_BASE_URL?: string;
    readonly VITE_API_TIMEOUT_MS?: string;
    readonly VITE_REVERB_APP_KEY?: string;
    readonly VITE_REVERB_HOST?: string;
    readonly VITE_REVERB_PORT?: string;
    readonly VITE_REVERB_SCHEME?: string;
    readonly VITE_APP_ENV?: string;
    readonly VITE_RELEASE_VERSION?: string;
    readonly VITE_ADMIN_FEATURES?: string;
  }
}
